/* ============================================
   NotaModal — crear o editar una nota interna del chat

   La fecha se elige, y eso es toda la feature: una nota del sabado se escribe el
   lunes y tiene que aparecer el sabado. Arranca en "ahora" para el caso comun.
   ============================================ */
import { useState } from 'react'
import { Modal, FormField, Button } from '@/components/ui'
import { IconPaperclip, IconX } from '@/components/icons/Icons'
import { showToast } from '@/components/layout/Toast'
import { useApi } from '@/hooks/useApi'
import type { ConversacionNota } from '@/lib/types'

interface NotaModalProps {
  open: boolean
  conversacionId: number
  /** La nota a editar, o null para crear una nueva. */
  nota: ConversacionNota | null
  onClose: () => void
  onSaved: () => void
}

/** Date → los dos valores que piden <input type="date"> y <input type="time">, en hora local. */
function partesLocales(d: Date): { dia: string; hora: string } {
  const p = (n: number) => String(n).padStart(2, '0')
  return {
    dia: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    hora: `${p(d.getHours())}:${p(d.getMinutes())}`,
  }
}

export default function NotaModal({ open, conversacionId, nota, onClose, onSaved }: NotaModalProps) {
  const api = useApi()
  const inicial = partesLocales(nota ? new Date(nota.fecha) : new Date())

  const [contenido, setContenido] = useState(nota?.contenido ?? '')
  const [dia, setDia] = useState(inicial.dia)
  const [hora, setHora] = useState(inicial.hora)
  const [archivos, setArchivos] = useState<File[]>([])
  const [guardando, setGuardando] = useState(false)

  async function guardar() {
    const texto = contenido.trim()
    if (!texto) {
      showToast('Escribe algo en la nota', 'error')
      return
    }

    const fecha = new Date(`${dia}T${hora}`)
    if (isNaN(fecha.getTime())) {
      showToast('La fecha no es valida', 'error')
      return
    }

    setGuardando(true)
    try {
      const guardada = nota
        ? await api.put<ConversacionNota>(`/api/chat/notas/${nota.id}`, {
            contenido: texto, fecha: fecha.toISOString(),
          })
        : await api.post<ConversacionNota>(`/api/chat/conversaciones/${conversacionId}/notas`, {
            contenido: texto, fecha: fecha.toISOString(),
          })

      // Los adjuntos van despues: la nota tiene que existir para colgarlos de ella.
      for (const file of archivos) {
        const fd = new FormData()
        fd.append('file', file)
        await api.upload(`/api/chat/notas/${guardada.id}/adjuntos`, fd)
      }

      showToast(nota ? 'Nota actualizada' : 'Nota agregada', 'success')
      onSaved()
      onClose()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo guardar la nota', 'error')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={nota ? 'Editar nota' : 'Nueva nota'}
      width={520}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={guardando}>Cancelar</Button>
          <Button variant="primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <FormField label="Nota" hint="Solo la ve el equipo. No se le envia al cliente.">
        <textarea
          className="form-textarea"
          rows={5}
          value={contenido}
          onChange={(e) => setContenido(e.target.value)}
          placeholder="Que paso, con las palabras que usarias para contarselo a alguien del equipo"
          autoFocus
        />
      </FormField>

      <FormField
        label="Fecha y hora"
        hint="Donde va a aparecer dentro del chat. Cambiala para dejar la nota en el dia en que paso."
      >
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" className="form-input" value={dia} onChange={(e) => setDia(e.target.value)} />
          <input type="time" className="form-input" value={hora} onChange={(e) => setHora(e.target.value)} />
        </div>
      </FormField>

      <FormField label="Adjuntos" hint="Capturas o archivos, hasta 25 MB cada uno.">
        <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
          <IconPaperclip size={15} />
          <span>Agregar archivo</span>
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => {
              setArchivos((prev) => [...prev, ...Array.from(e.target.files ?? [])])
              e.target.value = ''
            }}
          />
        </label>

        {archivos.length > 0 && (
          <div className="nota-modal__archivos">
            {archivos.map((f, i) => (
              <span key={i} className="nota-modal__archivo">
                {f.name}
                <button
                  type="button"
                  onClick={() => setArchivos((prev) => prev.filter((_, j) => j !== i))}
                  aria-label={`Quitar ${f.name}`}
                >
                  <IconX size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </FormField>
    </Modal>
  )
}
